<template>
  <div>
    <CustomModalComponent
      class="form-modal"
      :visible.sync="visible"
      :fullscreen="true"
      :show-close="false"
      :modal="false"
      v-loading="formProcessing"
    >
      <div id="edit-modal-header" class="modal-header">
        <div class="modal-header-layout flex grid-6 align-center">
          <h1 class="modal-title col-2">{{ context }} Account</h1>
          <div class="modal-actions col-2">
            <el-button
              type="primary"
              :disabled="parkinglotInUse"
              @click="handleSubmit"
            >
              {{ context === "Edit" ? "Save" : context }}
            </el-button>
            <el-button type="default" @click="handleClose">Cancel</el-button>
          </div>
        </div>
      </div>

      <!-- ⭐️ CREATE FORM ⭐️ -->
      <div v-show="context === 'Create'">
        <el-form style="margin-top: 25px">
          <el-tabs v-model="createActiveTab">
            <el-tab-pane label="General" name="General">
              <el-checkbox v-model="form.mfaEnabled">
                Enforce two-Factor authentication for organization</el-checkbox
              >

              <el-checkbox v-model="form.isResidential"
                >Residential account</el-checkbox
              >

              <div class="inline-form-group">
                <el-col :span="13">
                  <el-input v-model="form.name" placeholder="Enter name">
                    <template slot="prefix">Name</template>
                  </el-input>
                </el-col>
                <el-col :span="1" />
                <el-col :span="10">
                  <el-input
                    v-if="!isResellerAccount"
                    v-model="form.billingCode"
                    placeholder="Enter billing id"
                    type="number"
                  >
                    <template slot="prefix">Billing id</template>
                  </el-input>
                </el-col>
              </div>

              <DadsSelect
                clearable
                filterable
                v-model="form.timeZone"
                :value="form.timeZone"
                placeholder="Select time zone"
              >
                <template slot="prefix">Time zone</template>
                <el-option
                  v-for="(time, index) in timezoneOptions"
                  :key="index"
                  :label="time.name"
                  :value="time.name"
                />
              </DadsSelect>

              <DadsSelect
                clearable
                filterable
                v-show="isSuper && accountType === 'basic'"
                v-model="form.partnerId"
                placeholder="Select partner account"
                @change="checkIfResellerAccount"
              >
                <template slot="prefix">Partner account</template>
                <el-option
                  v-for="item in partnerOptions"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </DadsSelect>

              <h2 class="form-section-header">Bill Start Date</h2>
              <div class="inline-form-group">
                <el-col v-if="isPlatformOrPartnerAdmin" :span="10">
                  <el-date-picker
                    v-model="form.billDate"
                    placeholder="Bill date"
                    format="yyyy-MM-dd"
                    :pickerOptions="datePickerOptions"
                  />
                </el-col>
              </div>

              <div
                v-show="
                  isResellerAccount || isPartnerAccountCreation || userIsPartner
                "
              >
                <h2 class="form-section-header">Billing information</h2>
                <el-tag class="mb-1 mt-5" type="primary" v-if="userIsPartner">
                  <i class="el-icon-info" style="margin-right: 4px" />
                  May not be required
                </el-tag>
                <div class="inline-form-group">
                  <el-col :span="12">
                    <el-input
                      placeholder="Enter first name"
                      v-model="addressFields.firstName"
                    >
                      <template slot="prefix"> First name </template>
                    </el-input>
                  </el-col>
                  <el-col :span="1" />
                  <el-col :span="11">
                    <el-input
                      placeholder="Enter last name"
                      v-model="addressFields.lastName"
                    >
                      <template slot="prefix"> Last name </template>
                    </el-input>
                  </el-col>
                </div>
                <div class="inline-form-group">
                  <el-col :span="12">
                    <el-input
                      placeholder="Enter email"
                      v-model="addressFields.email"
                    >
                      <template slot="prefix"> Billing email </template>
                    </el-input>
                  </el-col>
                  <el-col :span="1" />
                  <el-col :span="11">
                    <el-input
                      placeholder="Enter phone number"
                      v-model="addressFields.phoneNumber"
                    >
                      <template slot="prefix"> Phone number </template>
                    </el-input>
                  </el-col>
                </div>
                <el-input
                  name="autoAddress"
                  v-model="address"
                  placeholder="Search for address"
                >
                  <template slot="prefix">Search address</template>
                </el-input>
                <div class="address-fields" v-if="addressFieldsActive">
                  <div class="inline-form-group">
                    <el-col :span="24">
                      <el-input
                        v-model="addressFields.street"
                        placeholder="Enter street number"
                      >
                        <template slot="prefix">Street number</template>
                      </el-input>
                    </el-col>
                  </div>
                  <div class="inline-form-group">
                    <el-col :span="11">
                      <el-input
                        v-model="addressFields.city"
                        placeholder="Enter city"
                      >
                        <template slot="prefix">City</template>
                      </el-input>
                    </el-col>
                    <el-col :span="1"></el-col>
                    <el-col :span="5">
                      <el-input
                        v-model="addressFields.state"
                        placeholder="Enter state"
                      >
                        <template slot="prefix">State</template>
                      </el-input>
                    </el-col>
                    <el-col :span="1"></el-col>
                    <el-col :span="6">
                      <el-input
                        v-model="addressFields.postalCode"
                        placeholder="Enter zip"
                      >
                        <template slot="prefix">Zip code</template>
                      </el-input>
                    </el-col>
                  </div>
                </div>
                <el-checkbox v-model="addressFields.shippingSameAsBilling">
                  Shipping address same as billing address
                </el-checkbox>
                <div v-if="!addressFields.shippingSameAsBilling">
                  <h2 class="form-section-header">Shipping address</h2>
                  <div class="inline-form-group">
                    <el-col :span="24">
                      <el-input
                        v-model="addressFields.shippingStreet"
                        placeholder="Enter street number"
                      >
                        <template slot="prefix">Street number</template>
                      </el-input>
                    </el-col>
                  </div>
                  <div class="inline-form-group">
                    <el-col :span="11">
                      <el-input
                        v-model="addressFields.shippingCity"
                        placeholder="Enter city"
                      >
                        <template slot="prefix">City</template>
                      </el-input>
                    </el-col>
                    <el-col :span="1"></el-col>
                    <el-col :span="5">
                      <el-input
                        v-model="addressFields.shippingState"
                        placeholder="Enter state"
                      >
                        <template slot="prefix">State</template>
                      </el-input>
                    </el-col>
                    <el-col :span="1"></el-col>
                    <el-col :span="6">
                      <el-input
                        v-model="addressFields.shippingPostalCode"
                        placeholder="Enter zip"
                      >
                        <template slot="prefix">Zip code</template>
                      </el-input>
                    </el-col>
                  </div>
                </div>
              </div>

              <SMSBrandingForm
                :clearForm="clearForm"
                :onSubmit="onSubmit"
                @onSubmit="onCreateFormSubmit"
              />
            </el-tab-pane>

            <el-tab-pane
              v-if="standardProducts.length > 0"
              label="Pricing"
              name="Pricing"
            >
              <el-checkbox v-model="verifyPricing"
                >Verify product pricing</el-checkbox
              >
              <h2 class="form-section-header">Voice Products</h2>
              <el-input
                v-for="(item, index) in standardProducts"
                @change="(val) => cleanProductPrice(val, index)"
                :readonly="!canModifyPricing"
                :key="item.index"
                v-model="standardProducts[index].price"
                placeholder="Enter price"
              >
                <template slot="prefix">
                  {{ item.name }}
                </template>
              </el-input>

              <h2 class="form-section-header">Custom Products</h2>
              <div v-if="customProducts.length">
                <div
                  v-for="(item, index) in customProducts"
                  class="pb-10 custom-product-group"
                  :key="index"
                >
                  <div class="inline-form-group">
                    <el-col :span="12">
                      <dads-select
                        filterable
                        :disabled="!canModifyPricing"
                        v-model="customProducts[index].productId"
                        placeholder="Select product"
                      >
                        <template slot="prefix">Product</template>
                        <el-option
                          v-for="(time, index) in customProductOptions"
                          :key="index"
                          :label="time.name"
                          :value="time.id"
                        />
                      </dads-select>
                    </el-col>
                    <el-col :span="1" />
                    <el-col :span="5">
                      <el-input
                        :readonly="!canModifyPricing"
                        v-model="customProducts[index].price"
                        @change="(val) => cleanCustomProductPrice(val, index)"
                        placeholder="Enter price"
                      >
                        <template slot="prefix"> Price </template>
                      </el-input>
                    </el-col>
                    <el-col :span="1" />
                    <el-col :span="5">
                      <el-input
                        :readonly="!canModifyPricing"
                        v-model="customProducts[index].customQuantity"
                        placeholder="Enter quantity"
                      >
                        <template slot="prefix"> Quantity </template>
                      </el-input>
                    </el-col>
                  </div>
                  <div class="inline-form-group">
                    <el-col :span="20">
                      <el-input
                        :readonly="!canModifyPricing"
                        v-model="customProducts[index].customDescription"
                        placeholder="Enter description"
                      >
                        <template slot="prefix"> Description </template>
                      </el-input>
                    </el-col>
                    <el-col :span="1" />
                    <el-col :span="3" class="text-right">
                      <el-tooltip placement="top" content="Remove product">
                        <el-button
                          @click="
                            removeCustomProduct(index, customProducts[index].id)
                          "
                          icon="el-icon-delete"
                        />
                      </el-tooltip>
                    </el-col>
                  </div>
                  <hr class="mt-20" />
                </div>
              </div>
              <el-button
                type="text"
                icon="el-icon-circle-plus-outline"
                @click="addCustomProduct"
              >
                Add a product
              </el-button>
            </el-tab-pane>
            <el-tab-pane name="Conduits" label="Conduits">
              <el-input
                v-model="form.conduitPrefix"
                placeholder="Enter conduit prefix"
              >
                <template slot="prefix">Conduit Prefix</template>
              </el-input>
              <el-tag class="mb-1 mt-5" type="warning">
                <i class="el-icon-info" style="margin-right: 4px" />
                Using a conduit only works when dialing 6 digits or less
              </el-tag>
              <h2 class="form-section-header">Select Conduits</h2>
              <el-tag class="mb-1 mt-5" type="warning">
                <i class="el-icon-info" style="margin-right: 4px" />
                Only accounts with conduit prefixes are listed.
              </el-tag>

              <DadsTransfer
                v-model="form.conduitAccounts"
                :data="conduitAccountOptions"
                :titles="['Available', 'Assigned']"
                filter-placeholder="Enter keyword"
                filterable
                @remoteSearch="remoteSearchConduits"
              />
            </el-tab-pane>

            <el-tab-pane name="PartnerSettings" v-if="isPartnerAccountCreation">
              <span slot="label">Partner Settings</span>
              <div>
                <el-input v-model="partnerForm.name" placeholder="Name">
                  <template slot="prefix">Name</template>
                </el-input>
                <DadsSelect v-model="partnerForm.type" placeholder="Type">
                  <el-option
                    v-for="(item, i) in partnerTypeOptions"
                    :key="i"
                    :label="item.label"
                    :value="item.value"
                  />
                  <template slot="prefix">Type</template>
                </DadsSelect>
                <el-checkbox v-model="partnerForm.enableCommission"
                  >Enable Commission</el-checkbox
                >

                <div v-if="partnerForm.type === 'WHITELABEL'">
                  <h2 class="form-section-header">10DLC Support Email</h2>

                  <el-input
                    name="autoAddressEdit"
                    v-model="supportEmail10DLC"
                    placeholder="Enter support email"
                  >
                    <template slot="prefix">10DLC Support email</template>
                  </el-input>

                  <h2 class="form-section-header">
                    Port-Out Notifications Email
                  </h2>

                  <el-input
                    name="autoAddressEdit"
                    v-model="portOutNotificationsEmail"
                    placeholder="Enter notifications email"
                  >
                    <template slot="prefix"
                      >Port-Out Notifications Email</template
                    >
                  </el-input>
                </div>

                <el-input
                  v-if="partnerForm.enableCommission"
                  type="number"
                  placeholder="Commission Rate"
                  v-model="partnerForm.commissionRate"
                >
                  <template slot="prefix">Commission Rate</template>
                </el-input>

                <div v-if="partnerForm.type === 'WHITELABEL'">
                  <h2 class="form-section-header">Partner Pricing</h2>

                  <el-input
                    v-for="(item, index) in partnerPricing"
                    v-model="item.price"
                    @change="(val) => cleanPartnerPrice(val, index)"
                    :key="index"
                    :placeholder="item.name"
                  >
                    <template slot="prefix">
                      {{ item.name }}
                    </template>
                  </el-input>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </el-form>
      </div>

      <!-- ⭐️ EDIT FORM ⭐️ -->
      <el-form style="margin-top: 20px" v-show="context === 'Edit'">
        <recent-audits :record="currentRecord" service="Account" />
        <el-tabs v-model="activeTab">
          <!-- ⭐️ GENERAL ⭐️ -->
          <el-tab-pane label="General" name="General">
            <el-checkbox v-model="editForm.mfaEnabled">
              Enforce two-factor authentication for organization</el-checkbox
            >

            <el-checkbox v-if="isPlatformOrPartnerAdmin" v-model="editForm.disabled"
              >Disable account</el-checkbox
            >
            <el-checkbox v-model="editForm.isResidential"
              >Residential account</el-checkbox
            >
            <div class="inline-form-group">
              <el-col :span="13">
                <el-input v-model="editForm.name" placeholder="Enter name">
                  <template slot="prefix">Name</template>
                </el-input>
              </el-col>
              <el-col :span="1" />
              <el-col :span="10">
                <el-input
                  v-model="editForm.billingCode"
                  :readonly="!isWhiteLabel"
                  placeholder="Enter billing id"
                >
                  <template slot="prefix">Billing id</template>
                </el-input>
              </el-col>
            </div>

            <dads-select
              clearable
              filterable
              v-model="editForm.timeZone"
              :value="editForm.timeZone"
              placeholder="Select time zone"
            >
              <template slot="prefix">Time zone</template>
              <el-option
                v-for="(time, index) in timezoneOptions"
                :key="index"
                :label="time.name"
                :value="time.name"
              />
            </dads-select>

            <DadsSelect
              v-if="isSuper"
              clearable
              filterable
              :disabled="context === 'Edit'"
              v-model="editForm.partnerId"
              placeholder="Select partner account"
            >
              <template slot="prefix">Partner account</template>
              <el-option
                v-for="item in partnerOptions"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </DadsSelect>

            <div v-show="!isBillingV2">
              <h2 class="form-section-header">Bill Start Date</h2>
              <div class="inline-form-group">
                <el-col :span="10">
                  <el-date-picker
                    v-model="editForm.billDate"
                    placeholder="Bill date"
                    format="yyyy-MM-dd"
                    :pickerOptions="datePickerOptions"
                    :disabled="!isPlatformOrPartnerAdmin"
                  ></el-date-picker>
                </el-col>
              </div>
            </div>

            <div v-show="!isWhiteLabel && !isBillingV2">
              <h2 class="form-section-header">Billing information</h2>
              <div class="inline-form-group">
                <el-col :span="12">
                  <el-input
                    placeholder="Enter first name"
                    v-model="addressFields.firstName"
                  >
                    <template slot="prefix"> First name </template>
                  </el-input>
                </el-col>
                <el-col :span="1" />
                <el-col :span="11">
                  <el-input
                    placeholder="Enter last name"
                    v-model="addressFields.lastName"
                  >
                    <template slot="prefix"> Last name </template>
                  </el-input>
                </el-col>
              </div>
              <div class="inline-form-group">
                <el-col :span="12">
                  <el-input
                    placeholder="Enter email"
                    v-model="addressFields.email"
                  >
                    <template slot="prefix"> Billing email </template>
                  </el-input>
                </el-col>
                <el-col :span="1" />
                <el-col :span="11">
                  <el-input
                    placeholder="Enter phone number"
                    v-model="addressFields.phoneNumber"
                  >
                    <template slot="prefix"> Phone number </template>
                  </el-input>
                </el-col>
              </div>
              <el-input
                name="autoAddressEdit"
                v-model="address"
                placeholder="Search for address"
              >
                <template slot="prefix">Search address</template>
              </el-input>
              <div class="address-fields" v-if="addressFieldsActive">
                <div class="inline-form-group">
                  <el-col :span="24">
                    <el-input
                      v-model="addressFields.street"
                      placeholder="Enter street number"
                    >
                      <template slot="prefix">Street number</template>
                    </el-input>
                  </el-col>
                </div>
                <div class="inline-form-group">
                  <el-col :span="11">
                    <el-input
                      v-model="addressFields.city"
                      placeholder="Enter city"
                    >
                      <template slot="prefix">City</template>
                    </el-input>
                  </el-col>
                  <el-col :span="1"></el-col>
                  <el-col :span="5">
                    <el-input
                      v-model="addressFields.state"
                      placeholder="Enter state"
                    >
                      <template slot="prefix">State</template>
                    </el-input>
                  </el-col>
                  <el-col :span="1"></el-col>
                  <el-col :span="6">
                    <el-input
                      v-model="addressFields.postalCode"
                      placeholder="Enter zip"
                    >
                      <template slot="prefix">Zip code</template>
                    </el-input>
                  </el-col>
                </div>
              </div>
              <el-checkbox v-model="addressFields.shippingSameAsBilling">
                Shipping address same as billing address
              </el-checkbox>
              <div v-if="!addressFields.shippingSameAsBilling">
                <h2 class="form-section-header">Shipping address</h2>
                <div class="inline-form-group">
                  <el-col :span="24">
                    <el-input
                      v-model="addressFields.shippingStreet"
                      placeholder="Enter street number"
                    >
                      <template slot="prefix">Street number</template>
                    </el-input>
                  </el-col>
                </div>
                <div class="inline-form-group">
                  <el-col :span="11">
                    <el-input
                      v-model="addressFields.shippingCity"
                      placeholder="Enter city"
                    >
                      <template slot="prefix">City</template>
                    </el-input>
                  </el-col>
                  <el-col :span="1"></el-col>
                  <el-col :span="5">
                    <el-input
                      v-model="addressFields.shippingState"
                      placeholder="Enter state"
                    >
                      <template slot="prefix">State</template>
                    </el-input>
                  </el-col>
                  <el-col :span="1"></el-col>
                  <el-col :span="6">
                    <el-input
                      v-model="addressFields.shippingPostalCode"
                      placeholder="Enter zip"
                    >
                      <template slot="prefix">Zip code</template>
                    </el-input>
                  </el-col>
                </div>
              </div>
            </div>

            <h2 class="form-section-header">Select music on hold</h2>
            <dads-select
              filterable
              v-model="editForm.defaultMoh"
              placeholder="Select music on hold"
            >
              <template slot="prefix">Music on hold</template>
              <el-option :value="0" label="System default" />
              <el-option
                v-for="(item, i) in musiconholdOpts"
                :key="i"
                :label="item.name"
                :value="item.id"
              />
            </dads-select>

            <new-musiconhold
              :tenantId="recordId"
              :clearForm="clearMusiconholdState"
              @musiconholdCreated="onMusiconholdCreation"
            />
            <br />

            <SMSBrandingForm
              v-if="brandIsNotAssociated"
              :clearForm="clearForm"
              :onSubmit="onSubmit"
              @onSubmit="onEditFormSubmit"
            />
          </el-tab-pane>

          <!-- ⭐️ SETTINGS ⭐️ -->
          <el-tab-pane label="Settings" name="settings">
            <el-checkbox v-model="editForm.enablePassword">
              Enable password prompt for retrieving voicemails
            </el-checkbox>

            <el-checkbox v-model="editForm.sameRing">
              Enable same ring for internal and external calling
            </el-checkbox>

            <div class="inline-form-group">
              <el-col :span="12">
                <el-select
                  v-model="editForm.incomingRingsCount"
                  placeholder="Select maximum incoming rings"
                >
                  <el-option
                    v-for="(n, i) in 60"
                    :key="i"
                    :label="n"
                    :value="n * 5"
                  />
                  <template slot="prefix">Maximum incoming rings</template>
                </el-select>
              </el-col>
              <el-col :span="1" />
              <el-col :span="11">
                <el-select
                  v-model="editForm.parkinglotTimeout"
                  placeholder="Select parking lot timeout"
                >
                  <el-option
                    v-for="(n, i) in 70"
                    :key="i"
                    :label="n * 10"
                    :value="n * 10"
                  />
                  <template slot="prefix">Parking lot timeout</template>
                </el-select>
              </el-col>
            </div>

            <el-select
              v-model="editForm.callWaiting"
              placeholder="Select call waiting setting"
            >
              <el-option
                v-for="(item, i) in callWaitingOptions"
                :key="i"
                :label="item.label"
                :value="item.value"
              />
              <template slot="prefix">Call waiting</template>
            </el-select>
            <div class="inline-form-group">
              <el-col :span="21">
                <el-select
                  v-model="editForm.parkinglot"
                  placeholder="Select total parking lots"
                  @change="parkinglotInUse = false"
                >
                  <div v-for="(n, i) in 300" :key="i">
                    <el-option v-if="n > 3" :label="n" :value="n"></el-option>
                  </div>
                  <template slot="prefix">Parking lot count</template>
                </el-select>
              </el-col>
              <el-col :span="3">
                <div v-if="parkinglotInUse" class="el-input-group__append">
                  In use!
                </div>
              </el-col>
            </div>

            <el-select
              v-if="isSuper"
              v-model="editForm.rpId"
              placeholder="Select routing profile"
            >
              <el-option
                v-for="(item, i) in routingProfileOptions"
                :key="i"
                :label="item.name"
                :value="item.id"
              />
              <template slot="prefix">Routing profile</template>
            </el-select>

            <div v-if="isSuper">
              <h2 class="form-section-header">Recording storage settings</h2>
              <el-select
                v-model="editForm.recordingStorage"
                placeholder="Select recording storage"
              >
                <el-option
                  v-for="(item, i) in recordingStorageOptions"
                  :key="i"
                  :label="item.label"
                  :value="item.value"
                />
                <template slot="prefix">Recording storage</template>
              </el-select>
              <div v-if="editForm.recordingStorage === 'AWS3'">
                <div class="inline-form-group">
                  <el-col :span="12">
                    <el-input
                      v-model="editForm.recordingHost"
                      placeholder="Enter recording host"
                    >
                      <template slot="prefix">Recording host</template>
                    </el-input>
                  </el-col>
                  <el-col :span="1" />
                  <el-col :span="11">
                    <el-input
                      v-model="editForm.recordingUser"
                      placeholder="Enter recording user"
                    >
                      <template slot="prefix">Recording user</template>
                    </el-input>
                  </el-col>
                </div>
                <div class="inline-form-group">
                  <el-col :span="12">
                    <el-input
                      v-model="editForm.recordingPassword"
                      placeholder="Enter recording password"
                    >
                      <template slot="prefix">Recording password</template>
                    </el-input>
                  </el-col>
                  <el-col :span="1" />
                  <el-col :span="11">
                    <el-input
                      v-model="editForm.recordingDir"
                      placeholder="Enter recording directory"
                    >
                      <template slot="prefix">Recording directory</template>
                    </el-input>
                  </el-col>
                </div>
              </div>
              <h2 class="form-section-header">Queue Settings</h2>
              <div class="inline-form-group">
                <el-checkbox
                  v-model="editForm.globalWrapUpEnable"
                  margin-right="100px"
                >
                  Enable global agent wrap up time
                  <el-popover trigger="hover" width="300px">
                    <i
                      class="more-info-icon el-icon-question"
                      slot="reference"
                      placement="bottom-start"
                    />
                    <p class="black-text mb-0 mt-0">
                      This setting enforces wrap up time for agents globally
                      <br />
                      instead of a per queue basis
                    </p>
                  </el-popover>
                </el-checkbox>
              </div>
              <div class="inline-form-group" v-if="editForm.globalWrapUpEnable">
                <el-col :span="12">
                  <el-input
                    type="input"
                    v-model="editForm.globalWrapUp"
                    placeholder="Wrap Up Time"
                    clearable
                  >
                  </el-input>
                  <el-tag class="mb-1 mt-5" type="warning">
                    <i class="el-icon-info" style="margin-right: 4px" />
                    Enter Value in Seconds.
                  </el-tag>
                </el-col>
              </div>
              <div>
                <h3 class="mb-0 mt-10 black-text">Queue Pause Reasons</h3>
                <DadsSelect
                  v-model="editForm.queuePauseReasons"
                  placeholder="Queue Pause Reasons"
                  filterable
                  multiple
                  allow-create
                >
                  <template slot="prefix" style="opacity: 0"
                    >Pause Reasons</template
                  >
                </DadsSelect>
              </div>
            </div>
          </el-tab-pane>

          <!-- ⭐️ INTEGRATIONS ⭐️ -->
          <el-tab-pane label="Integrations" name="Integrations">
            <h2 class="form-section-header">CRM Integrations</h2>
            <el-checkbox-group
              v-model="selectedIntegrations"
              class="flex mt-10"
            >
              <el-checkbox
                class="integration-checkbox pl-5"
                border
                v-for="item in integrationOptions"
                :key="item.id"
                :label="item.id"
              >
                <div class="integration-logo">
                  <img
                    :src="item.logoPath"
                    :alt="item.name"
                    :title="item.name"
                  />
                </div>
              </el-checkbox>
            </el-checkbox-group>

            <!--Sales Force Domain-->
            <h3 class="black-text mb-0">Salesforce Base URL</h3>
            <div class="inline-form-group">
              <el-col>
                <el-input v-model="salesforceDomain" placeholder="Domain">
                  <template slot="prefix">Domain</template>
                </el-input>
              </el-col>
            </div>

            <!--Enable CTI Task Gen-->
            <div>
              <h3 class="black-text mb-0">CTI Task Generation</h3>
              <el-select v-model="salesforceCTITasks" placeholder="Select">
                <el-option
                  v-for="(item, i) in taskGenerationOptions"
                  :key="i"
                  :label="item"
                  :value="item"
                />
                <template slot="prefix">Task Generation</template>
              </el-select>
            </div>

            <hr class="mv-20" />
            <h2 class="form-section-header">Call Outcomes</h2>
            <div v-loading="outcomeProcessing">
              <el-select
                v-model="callOutcomes"
                value-key="name"
                multiple
                allow-create
                filterable
                placeholder="Specify call outcomes"
                @change="onOutcomesChange"
              >
                <el-option
                  v-for="item in outcomeOptions"
                  :key="item.id"
                  :label="item.label"
                  :value="item.label"
                />
              </el-select>
            </div>

            <hr class="mv-20" />
            <h2 class="form-section-header">Webhooks</h2>
            <div v-for="(item, i) in editForm.webhooks" :key="i">
              <div class="webhook mt-20">
                <h3 class="form-section-header">Webhook {{ i + 1 }}</h3>
                <div class="inline-form-group">
                  <el-col :span="21">
                    <el-select v-model="item.type" placeholder="Type">
                      <el-option
                        v-for="(item, i) in webhookTypeOptions"
                        :key="i"
                        :label="item"
                        :value="item"
                      />
                      <template slot="prefix">Type</template>
                    </el-select>
                  </el-col>
                </div>
                <div class="inline-form-group">
                  <el-col :span="21">
                    <el-input v-model="item.url" placeholder="Url">
                      <template slot="prefix">Url</template>
                    </el-input>
                  </el-col>
                </div>

                <el-tooltip
                  v-if="i === editForm.webhooks.length - 1"
                  class="webhook__remove-btn"
                  effect="dark"
                  :content="`Remove Webhook ${i + 1}`"
                  placement="top"
                >
                  <el-button @click="removeWebhook(i)" icon="el-icon-delete" />
                </el-tooltip>
              </div>
            </div>
            <div class="inline-form-group">
              <el-col class="mt-20" :span="21">
                <el-button
                  style="float: right"
                  :disabled="disableAddingWebhook"
                  @click="addWebHook"
                >
                  Add Web Hook
                </el-button>
              </el-col>
              <el-col :span="3" />
            </div>
          </el-tab-pane>
          <el-tab-pane label="Pricing" name="Pricing">
            <h2 class="form-section-header">Voice Products</h2>
            <el-input
              v-for="(item, index) in standardProducts"
              @change="(val) => cleanProductPrice(val, index)"
              :readonly="!canModifyPricing"
              :key="item.productId"
              v-model="standardProducts[index].price"
              placeholder="Enter price"
            >
              <template slot="prefix">
                {{ item.name }}
              </template>
            </el-input>

            <h2 class="form-section-header">Custom Products</h2>
            <div v-if="customProducts.length">
              <div
                v-for="(item, index) in customProducts"
                class="pb-10 custom-product-group"
                :key="index"
              >
                <div class="inline-form-group">
                  <el-col :span="12">
                    <dads-select
                      filterable
                      :disabled="!canModifyPricing"
                      v-model="customProducts[index].productId"
                      placeholder="Select product"
                    >
                      <template slot="prefix">Product</template>
                      <el-option
                        v-for="(time, index) in customProductOptions"
                        :key="index"
                        :label="time.name"
                        :value="time.id"
                      />
                    </dads-select>
                  </el-col>
                  <el-col :span="1" />
                  <el-col :span="5">
                    <el-input
                      :readonly="!canModifyPricing"
                      v-model="customProducts[index].price"
                      @change="(val) => cleanCustomProductPrice(val, index)"
                      placeholder="Enter price"
                    >
                      <template slot="prefix"> Price </template>
                    </el-input>
                  </el-col>
                  <el-col :span="1" />
                  <el-col :span="5">
                    <el-input
                      :readonly="!canModifyPricing"
                      v-model="customProducts[index].customQuantity"
                      placeholder="Enter quantity"
                    >
                      <template slot="prefix"> Quantity </template>
                    </el-input>
                  </el-col>
                </div>
                <div class="inline-form-group">
                  <el-col :span="20">
                    <el-input
                      :readonly="!canModifyPricing"
                      v-model="customProducts[index].customDescription"
                      placeholder="Enter description"
                    >
                      <template slot="prefix"> Description </template>
                    </el-input>
                  </el-col>
                  <el-col :span="1" />
                  <el-col :span="3" class="text-right">
                    <el-tooltip placement="top" content="Remove product">
                      <el-button
                        @click="
                          removeCustomProduct(index, customProducts[index].id)
                        "
                        icon="el-icon-delete"
                      />
                    </el-tooltip>
                  </el-col>
                </div>
                <hr class="mt-20" />
              </div>
            </div>
            <el-button
              type="text"
              icon="el-icon-circle-plus-outline"
              @click="addCustomProduct"
            >
              Add a product
            </el-button>
          </el-tab-pane>
          <el-tab-pane name="conduits" label="Conduits">
            <el-input
              v-model="editForm.conduitPrefix"
              placeholder="Conduit Prefix"
            >
              <template slot="prefix">Conduit Prefix</template>
            </el-input>
            <el-tag class="mb-1 mt-5" type="warning">
              <i class="el-icon-info" style="margin-right: 4px" />
              Using a conduit only works when dialing 6 digits or less
            </el-tag>
            <h2 class="form-section-header">Select Conduits</h2>
            <el-tag class="mb-1 mt-5" type="warning">
              <i class="el-icon-info" style="margin-right: 4px" />
              Only accounts with conduit prefixes are listed.
            </el-tag>

            <DadsTransfer
              v-model="editForm.conduitAccounts"
              :data="conduitAccountOptions"
              :titles="['Available', 'Assigned']"
              filter-placeholder="Enter keyword"
              filterable
              @remoteSearch="remoteSearchConduits"
            />
          </el-tab-pane>

          <!-- NOTES -->
          <el-tab-pane name="notes" label="Notes">
            <h2 class="form-section-header">Add / Edit Note</h2>
            <el-input
              id="note"
              type="textarea"
              placeholder="Enter a note"
              v-model="note"
              :autosize="{ minRows: 4 }"
            >
            </el-input>
            <div v-if="selectedNote" class="inline-form-group">
              <el-button
                v-if="userIsNoteOwner"
                v-loading="editingNote"
                :disabled="note.length === 0"
                @click="editNote"
                style="margin-left: auto"
                >Save</el-button
              >
              <el-button @click="cancelEdit">Add New</el-button>
            </div>
            <div v-else-if="note.length > 0" class="flex">
              <el-button
                style="margin-left: auto"
                v-loading="creatingNote"
                @click="createNote"
              >
                Add
              </el-button>
            </div>

            <div class="mt-30"></div>

            <div class="flex align-center mb-15">
              <el-tag>
                <i class="el-icon-info" style="margin-right: 4px" />
                Total: {{ totalRecords }}
              </el-tag>
              <div v-if="totalRecords > 0 && !this.search" class="box right">
                <el-pagination
                  layout="prev, pager, next"
                  small
                  background
                  :current-page="page"
                  :page-size="recordsPerPage"
                  :pager-count="5"
                  :total="totalRecords"
                  @current-change="pageSet"
                  @next-click="pageNext"
                  @prev-click="pagePrevious"
                />
              </div>
            </div>

            <div
              v-if="notesTableData.length"
              id="notes-list"
              style="max-height: 500px; overflow-y: auto"
              v-loading="notesLoading"
            >
              <div
                v-for="(item, index) in notesTableData"
                :key="index"
                class="black-text mb-10 pv-5 pr-10"
              >
                <div class="note-container">
                  <div class="note-header flex align-center">
                    <div>
                      <div class="note-creator">
                        {{ item.name }}
                      </div>
                      <div
                        class="note-created-date"
                        style="font-size: 12px; font-style: italic"
                      >
                        {{
                          new Intl.DateTimeFormat("en-us", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                            hour12: true
                          }).format(new Date(item.createdAt))
                        }}
                      </div>
                    </div>
                    <div class="note-action-buttons" style="margin-left: auto">
                      <el-button
                        class="table-action-button"
                        icon="el-icon-edit"
                        @click="viewNote(item)"
                      />
                      <el-button
                        class="table-action-button"
                        type="danger"
                        icon="el-icon-delete"
                        @click="deleteNote(item.id)"
                      />
                    </div>
                  </div>
                  <div class="note-content mt-15 mb-10">{{ item.note }}</div>
                </div>
                <hr />
              </div>
            </div>
            <div v-else class="black-text">No search results found!</div>
          </el-tab-pane>
        </el-tabs>
      </el-form>
    </CustomModalComponent>
  </div>
</template>

<script>
import axios from "axios";
import { mapActions } from "vuex";
import feathersClient, { apiV2Host } from "@/feathers/feathers-client";
import DadsSelect from "@/components/select";
import CustomModalComponent from "../../../../components/CustomModalComponent";
import { debounce } from "lodash";
import moment from "moment-timezone";
import NewMusiconhold from "./NewMusiconhold.vue";
import RecentAudits from "@/components/RecentAudits";
import { isEqual } from "lodash";
import SMSBrandingForm from "./SMSBrandingForm.vue";
import DadsTransfer from "@/components/transfer";
import { phoneNumberFormatter } from "@/globals/globals";

export default {
  name: "AccountForm",

  props: {
    context: { required: true },
    recordId: { default: "" },
    visible: { default: false },
    integrationOptions: { required: true },
    timezoneOptions: { required: true },
    routingProfileOptions: { required: true },
    partnerOptions: { required: true },
    defaultPricing: { required: true },
    customProductOptions: { required: true },
    accountType: { type: String, required: true },
    partnerProducts: { required: true, default: null }
  },

  components: {
    DadsSelect,
    DadsTransfer,
    NewMusiconhold,
    RecentAudits,
    CustomModalComponent,
    SMSBrandingForm
  },

  data() {
    return {
      datePickerOptions: {
        disabledDate(time) {
          const validBillingDays = ["01", "05", "10", "15", "20"];
          const isValidBillDay = validBillingDays.includes(
            moment(time).format("DD")
          );
          const isInPast = moment(time).add(1, "days").valueOf() < Date.now();
          return !isValidBillDay || isInPast;
        }
      },
      outboundCidNumbersOptions: [],
      phoneNumberQuery: null,
      basicNavTop: null,
      isResellerAccount: false,
      createActiveTab: "General",
      defaultProducts: [],
      clearMusiconholdState: false,
      parkinglotInUse: false,
      initialParkinglot: null,
      formProcessing: false,
      address: "",
      addressFieldsActive: false,
      locationOptions: ["BLDG", "FL", "STE", "APT"],
      partnerPricing: [],
      callWaitingOptions: [
        {
          label: "Always Dial",
          value: true
        },
        {
          label: "Check Status",
          value: false
        }
      ],
      recordingStorageOptions: [
        {
          label: "Use Default",
          value: ""
        },
        {
          label: "Amazon Web Services",
          value: "AWS3"
        }
      ],
      webhookTypeOptions: [
        "ANSWEREDCALL",
        "CALLRECORDING",
        "ENDCALL",
        "EXTENSIONCREATED",
        "EXTENSIONDELETED",
        "FAXRECEIVED",
        "INBOUNDSMS",
        "NEWVOICEMAIL",
        "STARTCALL",
        "TRANSCRIPTIONEND"
      ],
      taskGenerationOptions: ["Always", "Never", "User"],
      selectedIntegrations: [],
      standardProducts: [],
      customProducts: [],
      verifyPricing: false,
      customProductRemoveIds: [],
      supportEmail10DLC: "",
      portOutNotificationsEmail: "",
      addressFields: {
        street: "",
        city: "",
        state: "",
        postalCode: "",
        email: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        shippingSameAsBilling: true,
        shippingStreet: "",
        shippingCity: "",
        shippingState: "",
        shippingPostalCode: ""
      },
      form: {
        mfaEnabled: false,
        name: "",
        timeZone: "",
        billDate: "",
        billingCode: "",
        partnerId: null,
        conduitAccounts: [],
        conduitPrefix: null,
        isResidential: 0
      },
      partnerForm: {
        name: "",
        type: "",
        enableCommission: false,
        commissionRate: null
      },
      partnerTypeOptions: [
        {
          item: "Reseller",
          value: "RESELLER"
        },
        {
          item: "White Label",
          value: "WHITELABEL"
        }
      ],
      editForm: {
        mfaEnabled: false,
        name: "",
        timeZone: "",
        billingCode: "",
        partnerId: null,
        enablePassword: false,
        incomingRingsCount: null,
        parkinglotTimeout: null,
        callWaiting: false,
        disabled: false,
        isResidential: 1,
        parkinglot: null,
        recordingStorage: "",
        recordingHost: "",
        recordingUser: "",
        recordingPassword: "",
        recordingDir: "",
        rpId: null,
        defaultMoh: 0,
        sameRing: false,
        webhooks: [
          {
            type: "",
            url: ""
          }
        ],
        conduitAccounts: [],
        isConduitAccountsChanged: false,
        conduitPrefix: null,
        isConduitPrefixChanged: false,
        globalWrapUpEnable: false,
        globalWrapUp: null,
        billDate: null,
        queuePauseReasons: []
      },
      note: "",
      notesLoading: false,
      creatingNote: false,
      editingNote: false,
      selectedNote: null,
      page: 1,
      maxPage: 1,
      recordsPerPage: 75,
      totalRecords: 0,
      notesData: [],
      notesTableData: [],
      visibleActionsId: "",
      search: "",
      noteTableColumns: [
        {
          field: "userId",
          title: "User ID",
          minWidth: 50,
          showOverflow: false
        },
        {
          field: "name",
          title: "Name",
          minWidth: 50,
          showOverflow: false
        },
        {
          field: "note",
          title: "Note",
          minWidth: 200,
          showOverflow: true
        },
        {
          field: "createdAt",
          title: "Created At",
          minWidth: 150,
          sortable: true,
          showOverflow: true,
          slots: {
            default: ({ row }) => {
              return new Intl.DateTimeFormat("en-us", {
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "numeric",
                minute: "numeric",
                hour12: true
              }).format(new Date(row.createdAt));
            }
          }
        },
        {
          field: "actions",
          title: "",
          minWidth: "150",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={"edit-" + row.id}
                    icon="el-icon-edit"
                    onClick={() => this.viewNote(row)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    v-show={row.userId === this.user.id}
                    id={"delete-" + row.id}
                    type="danger"
                    icon="el-icon-delete"
                    size="mini"
                    onClick={() => this.deleteNote(row.id)}
                    class="table-action-button"
                  />
                </div>
              ];
            }
          }
        }
      ],

      currentRecord: null,
      activeTab: "General",
      callOutcomes: [],
      salesforceCTITasks: "",
      salesforceDomain: "",
      outcomeProcessing: false,
      outcomeOptions: [],
      musiconholdOpts: [],

      // ⭐️ SMS Branding props ⭐️ //
      clearForm: false,
      onSubmit: false,
      smsForm: null,

      // ⭐️ Conduit Accounts ⭐️ //
      conduitAccountOptions: [],
      conduitsQuery: null,
      isWhiteLabel: false,
      isBillingV2: false
    };
  },

  computed: {
    userIsNoteOwner() {
      return this.selectedNote && this.selectedNote.userId === this.user.id;
    },

    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    conduitAccounts() {
      return this.form.conduitAccounts;
    },

    isSuper() {
      if (!this.user) return false;

      return +this.user.userRole === 1;
    },

    isPlatformOrPartnerAdmin() {
      if (!this.user) return false;

      const { userRole } = this.user;
      return this.isSuper || +userRole === 11;
    },

    recordingStorage() {
      return this.editForm.recordingStorage;
    },

    parkinglot() {
      return this.editForm.parkinglot;
    },

    user() {
      return this.$store.state.auth.user;
    },

    userIsPartner() {
      if (this.user) {
        return +this.user.userRole === 11;
      }
      return false;
    },

    canModifyPricing() {
      if (this.recordId) {
        const { partnerId, userRole } = this.user;
        const recordPartnerId = this.currentRecord.partnerId;

        if (+userRole === 1) {
          return true;
        }

        if (+userRole === 3 || +userRole === 12) {
          return false;
        }

        if (+userRole === 11) {
          return partnerId === recordPartnerId;
        }
      }

      return true;
    },

    isQbCustomer() {
      if (this.currentRecord) {
        return this.currentRecord.isQbCustomer;
      }
      return false;
    },

    isPartnerAccountCreation() {
      return this.accountType === "partner";
    },

    brandIsNotAssociated() {
      return !(this.currentRecord && this.currentRecord.brandId);
    },

    disableAddingWebhook() {
      const lastIdx = this.editForm.webhooks.length - 1;
      const lastWebhook = this.editForm.webhooks[lastIdx];
      return !lastWebhook.type || !lastWebhook.url;
    }
  },

  watch: {
    context(context) {
      if (context === "Edit") {
        this.queryNotes();
      }
    },

    $route: {
      immediate: true,
      handler(to) {
        if (to.params.tenantId) {
          this.queryNotes();
        }
      }
    },

    page: debounce(function () {
      this.queryNotes();
    }, 500),

    search: debounce(function () {
      this.searchNotes();
    }, 300),

    phoneNumberQuery: debounce(function () {
      this.findPhoneNumbers(this.phoneNumberQuery);
    }, 500),

    recordId: {
      immediate: true,
      async handler(val) {
        if (val) {
          this.formProcessing = true;

          this.standardProducts = [];
          await this.populateFields();
          const params = {
            query: {
              tenantId: { $in: [this.recordId, 0] },
              $sort: { name: 1 }
            }
          };
          this.musiconholdOpts = await this.findMusiconholds(params);

          // Popuate selected conduit options
          this.queryConduitOptions();

          this.formProcessing = false;
        } else {
          const products = this.defaultPricing.map((item) => ({ ...item }));
          this.standardProducts = products.sort(
            (a, b) => a.productId - b.productId
          );
        }
      }
    },

    parkinglot: debounce(function (val) {
      if (val && this.initialParkinglot !== val) {
        const start = 71;
        const end = start + val - 1;

        for (let num = 71; num <= end; num++) {
          setTimeout(() => {
            feathersClient
              .service("check-number")
              .find({
                query: {
                  tenantId: this.currentRecord.id,
                  number: num
                }
              })
              .then((res) => {
                this.parkinglotInUse = res.inUse;
              });
          }, 500);
        }
      }
    }, 500),

    partnerProducts() {
      this.partnerPricing = this.partnerProducts ? this.partnerProducts : [];
    },

    visible(val) {
      if (val) {
        this.clearForm = false;
        this.onSubmit = false;
        this.queryConduitOptions();
      }
    },

    conduitsQuery: debounce(function () {
      this.queryConduitOptions();
    }, 10)
  },

  methods: {
    ...mapActions("tenants", {
      patchAccount: "patch",
      createAccount: "create",
      deleteAccount: "remove"
    }),

    ...mapActions("musiconholds", {
      findMusiconholds: "find"
    }),

    ...mapActions("partners", { createPartner: "create" }),

    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.viewNote(data.row);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    pageNext() {
      if (this.page < this.maxPage) {
        this.page += 1;
      }
    },

    pagePrevious() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },

    pageSet(page) {
      this.page = page;
    },

    viewNote(row) {
      this.selectedNote = row;
      this.note = row.note;
    },

    cancelEdit() {
      this.selectedNote = null;
      this.note = "";
    },

    searchNotes() {
      this.notesTableData = [...this.notesData];

      if (this.search && this.search.length) {
        const search = this.search.toLowerCase();
        this.notesTableData = this.notesData.filter(
          (note) =>
            note.note.toLowerCase().includes(search) ||
            note.name.toLowerCase().includes(search)
        );
      }

      this.totalRecords = this.notesTableData.length;
    },

    async editNote() {
      const noteBody = {
        note: this.note
      };

      try {
        await this.$confirm("Edit this Note?", "Warning", {
          confirmButtonText: "Yes",
          cancelButtonText: "Cancel"
        });

        this.editingNote = true;

        const token = this.$store.state.auth.accessToken;

        const res = await fetch(
          `${apiV2Host}/admin/tenants/notes/${this.selectedNote.id}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(noteBody)
          }
        );

        const data = await res.json();
        this.editingNote = false;

        if (res.ok && res.status === 200) {
          this.selectedNote = null;
          this.note = "";
          this.$notify({
            title: "Success",
            message: "Edited note successfully!",
            type: "success"
          });
          this.queryNotes();
          return;
        }

        const { message, name, validationErrors } = data;
        this.$notify({
          title: name,
          message: validationErrors ? validationErrors[0] : message,
          type: "error"
        });
      } catch (err) {
        this.editingNote = false;
      }
    },

    async createNote() {
      const noteBody = {
        note: this.note,
        tenantId: this.selectedTenant.id
      };

      try {
        await this.$confirm("Add Note?", "Warning", {
          confirmButtonText: "Yes",
          cancelButtonText: "Cancel"
        });

        this.creatingNote = true;

        const token = this.$store.state.auth.accessToken;

        const res = await fetch(`${apiV2Host}/admin/tenants/notes`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(noteBody)
        });

        const data = await res.json();
        this.creatingNote = false;

        if (res.ok && res.status === 200) {
          this.note = "";
          this.$notify({
            title: "Success",
            message: "Created note successfully!",
            type: "success"
          });
          this.queryNotes();
          return;
        }

        const { message, name, validationErrors } = data;
        this.$notify({
          title: name,
          message: validationErrors ? validationErrors[0] : message,
          type: "error"
        });
      } catch (err) {
        this.creatingNote = false;
      }
    },

    async queryNotes() {
      this.notesLoading = true;
      this.notesData = [];

      try {
        const token = this.$store.state.auth.accessToken;

        const res = await fetch(
          `${apiV2Host}/admin/tenants/notes?tenantId=${this.recordId}&page=${this.page}&recordsPerPage=${this.recordsPerPage}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${token}`
            }
          }
        );

        const { records, totalRecords, maxPage } = await res.json();
        this.totalRecords = totalRecords;
        this.maxPage = maxPage;
        this.notesData = [...records];
        this.notesTableData = [...records];

        this.notesLoading = false;
        const list = document.getElementById("notes-list");
        list.scrollTo({ top: 0 });
      } catch (err) {
        this.notesLoading = false;
      }
    },

    async deleteNote(noteId) {
      try {
        await this.$confirm("Delete a Note?", "Warning", {
          confirmButtonText: "Yes",
          cancelButtonText: "Cancel"
        });

        this.notesLoading = true;

        const token = this.$store.state.auth.accessToken;

        const res = await fetch(`${apiV2Host}/admin/tenants/notes/${noteId}`, {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`
          }
        });

        const data = await res.json();
        this.notesLoading = false;

        if (res.ok && res.status === 200) {
          this.note = "";
          this.$notify({
            title: "Success",
            message: "Deleted note successfully!",
            type: "success"
          });
          this.queryNotes();
          return;
        }

        const { message, name, validationErrors } = data;
        this.$notify({
          title: name,
          message: validationErrors ? validationErrors[0] : message,
          type: "error"
        });
      } catch (err) {
        this.notesLoading = false;
      }
    },

    generateRandomKey() {
      const generateRandomString = () => Math.random().toString(36).slice(2);
      return `${generateRandomString()}-${generateRandomString()}$${generateRandomString()}`;
    },

    remoteSearchPhoneNumbers(query) {
      this.phoneNumberQuery = query;
    },

    findPhoneNumbers(query) {
      const queryParams = {
        tenantId: this.recordId,
        $limit: 10,
        $sort: { number: 1 }
      };

      if (query && query.length) {
        const searchTerm = (query + "")
          .replace(/[\\"']/g, "\\$&")
          .replace(/\u0000/g, "\\0");

        queryParams.$or = [{ number: { $like: `%${searchTerm}%` } }];
      }

      feathersClient
        .service("dids")
        .find({
          query: queryParams
        })
        .then((res) => {
          this.outboundCidNumbersOptions = [...res];
        });
    },

    async queryCallOutcomes() {
      const token = this.$store.state.auth.accessToken;

      let records = [];

      try {
        const url = `${apiV2Host}/admin/call-outcomes?tenantId=${this.recordId}`;

        const res = await axios.get(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        });

        records = res.data;
      } catch (err) {
        //
      }

      return records;
    },

    async createCallOutcome(name) {
      if (!name) {
        return;
      }

      const createBody = {
        name,
        tenantId: this.recordId,
        description: ""
      };

      const url = `${apiV2Host}/admin/call-outcomes`;
      const token = this.$store.state.auth.accessToken;

      try {
        const res = await axios.post(url, createBody, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        });
        return res.data;
      } catch (err) {
        this.outcomeProcessing = false;
      }
    },

    async deleteCallOutcome(outcomeId) {
      if (!outcomeId) {
        return;
      }

      const url = `${apiV2Host}/admin/call-outcomes/${outcomeId}`;
      const token = this.$store.state.auth.accessToken;

      try {
        await axios.delete(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        });
      } catch (err) {
        this.outcomeProcessing = false;
      }
    },

    async onOutcomesChange(outcomes) {
      const newOutcome = outcomes.find(
        (outcome) =>
          !this.outcomeOptions.map(({ label }) => label).includes(outcome)
      );

      if (newOutcome) {
        const newRecord = await this.createCallOutcome(newOutcome);
        if (newRecord) {
          this.outcomeOptions.push({ ...newRecord, label: newRecord.name });
        }
        return;
      }

      let outcomeToDelete = null;
      let outcomeToDeleteIdx = null;

      for (let i = 0; i < this.outcomeOptions.length; i++) {
        const outcome = this.outcomeOptions[i];
        if (!outcomes.includes(outcome.label)) {
          outcomeToDelete = outcome;
          outcomeToDeleteIdx = i;
        }
      }

      if (outcomeToDelete) {
        await this.deleteCallOutcome(outcomeToDelete.id);
        this.outcomeOptions.splice(outcomeToDeleteIdx, 1);
      }
    },

    async populateFields() {
      if (this.recordId) {
        this.addressFieldsActive = true;
        const record = await feathersClient
          .service("tenants")
          .get(this.recordId);

        this.currentRecord = record;
        this.selectedIntegrations = [...this.currentRecord.integrations];
        this.isWhiteLabel = !!record.isWhiteLabel;
        this.isBillingV2 = !!record.isBillingV2;

        for (let key in record) {
          if (this.editForm.hasOwnProperty(key)) {
            if (key === "conduitAccounts") {
              this.editForm[key] = [...new Set(record[key])];
              continue;
            }

            if (key === "billDate") {
              this.editForm[key] = record[key]
                ? moment(record[key]).format("YYYY-MM-DD HH:mm:ss")
                : null;
              continue;
            }

            if (key === "timeZone") {
              const timezoneOption = this.timezoneOptions.find(
                (option) => option.id === record.tzId
              );
              this.editForm[key] = timezoneOption ? timezoneOption.name : null;
              continue;
            }

            this.editForm[key] = record[key];
          }
        }

        if (!record.queuePauseReasons) {
          this.editForm.queuePauseReasons = [];
        }

        this.editForm.mfaEnabled = record.mfaEnabled === 1;

        if (this.editForm.webhooks.length === 0) {
          this.addWebHook();
        }

        //populate the address info
        if (record.addressFields) {
          this.addressFields.street = record.addressFields.street;
          this.addressFields.postalCode = record.addressFields.postalCode;
          this.addressFields.city = record.addressFields.city;
          this.addressFields.state = record.addressFields.state;
          this.addressFields.email = record.addressFields.email;
          this.addressFields.firstName = record.addressFields.firstName;
          this.addressFields.lastName = record.addressFields.lastName;
          this.addressFields.phoneNumber = record.addressFields.phoneNumber;
          this.addressFields.shippingSameAsBilling =
            record.addressFields.shippingSameAsBilling !== false;
          this.addressFields.shippingStreet =
            record.addressFields.shippingStreet || "";
          this.addressFields.shippingCity =
            record.addressFields.shippingCity || "";
          this.addressFields.shippingState =
            record.addressFields.shippingState || "";
          this.addressFields.shippingPostalCode =
            record.addressFields.shippingPostalCode || "";
        }

        //populate the pricing information
        this.standardProducts = record.pricing
          .filter((item) => !item.isCustomProduct)
          .sort((a, b) => a.productId - b.productId);
        this.customProducts = record.pricing
          .filter((item) => item.isCustomProduct)
          .sort((a, b) => a.productId - b.productId);

        this.editForm.incomingRingsCount = Number(
          this.editForm.incomingRingsCount
        );

        this.editForm.disabled = this.editForm.disabled === "on";
        this.editForm.isResidential = this.editForm.isResidential === 1;

        if (!this.editForm.globalWrapUp || this.editForm.globalWrapUp === "") {
          this.editForm.globalWrapUpEnable = false;
          //this.editForm.globalWrapUp = null;
        } else {
          this.editForm.globalWrapUpEnable = true;
          this.editForm.globalWrapUp = this.editForm.globalWrapUp;
        }

        this.initialParkinglot = this.editForm.parkinglot;

        this.editForm.sameRing = record.sameRing;

        const { records } = await this.queryCallOutcomes();

        this.salesforceCTITasks = record.salesforceCTITasks;
        this.salesforceDomain = record.salesforceDomain;

        this.callOutcomes = records ? records.map((item) => item.name) : [];
        this.outcomeOptions = records
          ? records.map((item) => {
              return {
                id: item.id,
                label: item.name,
                disabled: false
              };
            })
          : [];
      }
    },

    checkIfResellerAccount(val) {
      const selectedPartner = this.partnerOptions.filter(
        (item) => item.id === val
      );
      const { type } = selectedPartner[0];
      this.isResellerAccount = type === "RESELLER";
    },

    addCustomProduct() {
      this.customProducts.push({
        productId: null,
        customQuantity: 0,
        customDescription: null,
        price: 0
      });
    },

    removeCustomProduct(index, id) {
      this.customProducts.splice(index, 1);
      if (id) {
        this.customProductRemoveIds.push(id);
      }
    },

    handleClose() {
      this.$emit("modalClosed");
      this.clearFormFields();
    },

    clearFormFields() {
      this.clearForm = true; // ⭐️ Prop to clear SMS Branding Form ⭐️ //
      this.formProcessing = false;
      this.smsForm = null;
      this.customProducts = [];
      this.verifyPricing = false;
      this.clearMusiconholdState = false;
      this.parkinglotInUse = false;
      this.initialParkinglot = false;
      this.activeTab = "General";
      this.createActiveTab = "General";
      this.currentRecord = null;
      this.selectedIntegrations = [];
      this.outcomeOptions = [];
      this.address = "";
      this.addressFieldsActive = false;
      this.callOutcomes = [];
      this.addressFields = {
        street: "",
        city: "",
        state: "",
        postalCode: "",
        email: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        shippingSameAsBilling: true,
        shippingStreet: "",
        shippingCity: "",
        shippingState: "",
        shippingPostalCode: ""
      };
      this.form = {
        mfaEnabled: false,
        name: "",
        timeZone: "",
        billDate: "",
        billingCode: "",
        partnerId: null,
        conduitAccounts: [],
        conduitPrefix: null,
        isResidential: 0
      };
      this.partnerForm = {
        name: "",
        type: "",
        enableCommission: false,
        commissionRate: null
      };
      this.editForm = {
        mfaEnabled: false,
        name: "",
        timeZone: "",
        billingCode: "",
        partnerId: null,
        enablePassword: false,
        incomingRingsCount: null,
        parkinglotTimeout: null,
        callWaiting: false,
        disabled: false,
        isResidential: 0,
        parkinglot: null,
        recordingStorage: "",
        recordingHost: "",
        recordingUser: "",
        recordingPassword: "",
        recordingDir: "",
        rpId: null,
        defaultMoh: 0,
        sameRing: false,
        webhooks: [
          {
            type: "",
            url: ""
          }
        ],
        conduitAccounts: [],
        isConduitAccountsChanged: false,
        conduitPrefix: null,
        isConduitPrefixChanged: false,
        globalWrapUpEnable: false,
        globalWrapUp: null,
        billDate: null,
        queuePauseReasons: []
      };
      this.musiconholdOpts = [];
      this.conduitAccountOptions = [];
      this.conduitsQuery = null;

      const products = this.defaultPricing.map((item) => ({ ...item }));
      this.standardProducts = products.sort(
        (a, b) => a.productId - b.productId
      );

      // Notes
      this.note = "";
      this.search = "";
      this.notesData = [];
      this.notesTableData = [];
    },

    numberFormatter(val) {
      return phoneNumberFormatter(val);
    },

    handleSubmit() {
      if (this.context === "Edit") {
        if (this.brandIsNotAssociated) {
          this.onSubmit = true;
          return;
        }

        this.handleEdit();
      }
      if (this.context === "Create") {
        this.onSubmit = true;
        this.handleCreate();
      }
    },

    onEditFormSubmit(smsForm) {
      if (this.context === "Edit") {
        this.smsForm = smsForm;
        this.handleEdit();
      }
    },

    onCreateFormSubmit(smsForm) {
      if (this.context === "Create") {
        this.smsForm = smsForm;
        this.handleCreate();
      }
    },

    handleEdit() {
      //check that all custom products have a product type selected.
      let invalidCustomProduct = false;
      for (let i = 0; i < this.customProducts.length; i++) {
        if (!this.customProducts[i].productId) {
          invalidCustomProduct = true;
        }
      }

      if (invalidCustomProduct) {
        this.$notify({
          title: "Invalid Product",
          message: "All custom products must have a product type",
          type: "error"
        });
        return;
      }

      let data = {
        ...this.editForm,
        integrations: this.selectedIntegrations,
        callOutcomes: [],
        products: [...this.standardProducts, ...this.customProducts],
        customProductRemoveIds: this.customProductRemoveIds,
        addressFields: this.addressFields,
        isQbCustomer: this.isQbCustomer,
        smsBranding: this.smsForm,
        salesforceCTITasks: this.salesforceCTITasks,
        salesforceDomain: this.salesforceDomain
      };

      const { billDate } = data;
      data.billDate = billDate
        ? moment(data.billDate).format("YYYY-MM-DD HH:mm:ss")
        : "";

      data.mfaEnabled = data.mfaEnabled ? 1 : 0;

      data.conduitPrefix = data.conduitPrefix
        ? data.conduitPrefix.toString()
        : null;

      data.isConduitPrefixChanged =
        data.conduitPrefix !== this.currentRecord.conduitPrefix;
      data.isConduitAccountsChanged = !isEqual(
        data.conduitAccounts,
        this.currentRecord.conduitAccounts
      );

      if (data.recordingStorage === null) {
        data.recordingStorage = this.currentRecord.recordingStorage;
      }

      if (data.rpId === null) {
        delete data.rpId;
      }

      data.disabled = data.disabled ? "on" : "";
      data.isResidential = data.isResidential ? 1 : 0;

      data.globalWrapUpEnable = data.globalWrapUpEnable ? true : false;
      if (data.globalWrapUpEnable === true) {
        data.globalWrapUp = data.globalWrapUp;
      } else {
        data.globalWrapUp = null;
      }

      if (data.recordingStorage !== "AWS3") {
        data.recordingStorage = "";
        data.recordingHost = "";
        data.recordingUser = "";
        data.recordingPassword = "";
        data.recordingDir = "";
      }

      if (data.webhooks.length === 1) {
        let count = 0;
        Object.keys(data.webhooks[0]).forEach((key) => {
          if (!data.webhooks[0][key]) {
            count++;
          }
        });
        if (Object.keys(data.webhooks[0]).length === count) {
          data.webhooks = [];
        }
      }
      this.$confirm("Update Account Details?", "Warning", {
        confirmButtonText: "Update",
        cancelButtonText: "Cancel"
      })
        .then(() => {
          this.formProcessing = true;
          this.patchAccount([this.recordId, data])
            .then(async (res) => {
              this.formProcessing = false;
              this.$notify({
                title: "Success",
                message: "Account " + res.name + " has been updated!",
                type: "success"
              });
              this.clearMusiconholdState = true;
              this.$emit("modalClosed");
              this.clearFormFields();
            })
            .catch((error) => {
              this.onSubmit = false;
              this.formProcessing = false;
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }
            });
        })
        .catch(() => {
          this.onSubmit = false;
        });
    },

    handleAccountCreation(data) {
      if (this.accountType === "partner") {
        const { billDate } = data;
        const billDay = +moment(billDate).format("DD");
        data.partnerForm.billDay = billDay;
        return this.createPartner(data);
      }

      return this.createAccount(data);
    },

    async handleCreate() {
      // Only allow creation if they have verified pricing
      if (!this.verifyPricing) {
        this.createActiveTab = "Pricing";
        this.$notify({
          title: "Verify Pricing",
          message: "Please review and verify the account pricing.",
          type: "warning"
        });
        return;
      }

      let data = {
        ...this.form,
        integrations: this.selectedIntegrations,
        callOutcomes: [],
        products: [...this.standardProducts, ...this.customProducts],
        smsBranding: this.smsForm,
        addressFields: this.addressFields
      };

      const { billDate } = data;
      data.billDate = billDate
        ? moment(data.billDate).format("YYYY-MM-DD HH:mm:ss")
        : "";

      data.mfaEnabled = data.mfaEnabled ? 1 : 0;
      data.isResidential = data.isResidential ? 1 : 0;

      if (+this.user.userRole === 11) {
        data.partnerId = this.user.partnerId;
      }

      let warnText = "";
      if (this.accountType === "partner") {
        data.isQbCustomer = true;

        const { enableCommission, commissionRate } = this.partnerForm;
        const partnerData = { ...this.partnerForm };
        partnerData.enableCommission = enableCommission ? 1 : 0;
        partnerData.commissionRate = enableCommission ? +commissionRate : null;
        partnerData.supportEmail10DLC =
          this.partnerForm.type === "WHITELABEL"
            ? this.supportEmail10DLC
            : "support@voxo.co";
        partnerData.portOutNotificationsEmail =
          this.partnerForm.type === "WHITELABEL"
            ? this.portOutNotificationsEmail
            : "support@voxo.co";

        // ⭐️ Merge Data ⭐️ //
        data = {
          ...data,
          partnerForm: partnerData,
          partnerPricing: this.partnerPricing
        };

        warnText = `Confirm Partner Account Creation?`;
      } else {
        warnText = `Confirm Basic Account Creation?`;
        data.isQbCustomer = this.isResellerAccount;
      }

      this.$confirm(warnText, "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.formProcessing = true;

          this.handleAccountCreation(data)
            .then(async (res) => {
              this.formProcessing = false;
              this.$notify({
                title: "Success",
                message: "User " + res.name + " has been created!",
                type: "success"
              });

              //if it was a partner account need to refresh available partners
              if (this.accountType === "partner") {
                this.$emit("refreshPartners");
              }

              this.$emit("modalClosed");
              this.clearFormFields();
            })
            .catch((error) => {
              this.formProcessing = false;
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }
            });
        })
        .catch(() => true);
    },

    deleteRecord(id) {
      this.$confirm(
        "Permanently delete this account? No Going Back!",
        "Warning",
        {
          confirmButtonText: "Yes",
          cancelButtonText: "Cancel",
          type: "warning"
        }
      )
        .then(() => {
          this.deleteAccount(id)
            .then((res) => {
              this.$notify({
                title: "Success",
                message: "Account " + res.name + " has been removed!",
                type: "success"
              });
              this.$emit("modalClosed");
              this.clearFormFields();
            })
            .catch((error) => {
              this.$notify({
                title: error.name,
                message: error.message,
                type: "error"
              });
            });
        })
        .catch(() => true);
    },

    addWebHook() {
      this.editForm.webhooks.push({
        type: "",
        url: ""
      });
    },

    removeWebhook(i) {
      this.editForm.webhooks.splice(i, 1);
      if (i === 0) {
        this.addWebHook();
      }
    },

    removeWebHook(idx) {
      this.editForm.webhooks.splice(idx, 1);
      if (idx === 0) {
        this.addWebHook();
      }
    },

    onMusiconholdCreation(musiconhold) {
      this.musiconholdOpts.push(musiconhold);
      this.editForm.defaultMoh = musiconhold.id;
    },

    populateAddressFields(obj) {
      this.addressFields.street = `${obj["street_number"]} ${obj["route"]}`;
      this.addressFields.city = obj["locality"];
      this.addressFields.state = obj["administrative_area_level_1"];
      this.addressFields.postalCode = obj["postal_code"];
      this.address =
        obj["street_number"] +
        " " +
        obj["route"] +
        " " +
        obj["locality"] +
        ", " +
        obj["administrative_area_level_1"] +
        " " +
        obj["postal_code"];
    },

    remoteSearchConduits(query) {
      this.conduitsQuery = query;
    },

    async queryConduitOptions() {
      const token = this.$store.state.auth.accessToken;

      try {
        let url = `${apiV2Host}/admin/tenants/conduits?recordsPerPage=500`;

        if (this.conduitsQuery) {
          url = `${url}&query=${this.conduitsQuery}`;
        }

        const res = await axios.get(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        });

        let { records } = res.data;

        this.conduitAccountOptions = records.map(({ id, name }) => ({
          key: id,
          label: name,
          disabled: false
        }));

        const conduitAccountOptionIds = records.map((item) => item.id);

        let selectedConduitAccounts = [];
        if (this.context === "Create") {
          selectedConduitAccounts = this.form.conduitAccounts;
        }

        if (this.context === "Edit") {
          selectedConduitAccounts = this.editForm.conduitAccounts;
        }

        const notInOptionIds = selectedConduitAccounts.filter(
          (id) => !conduitAccountOptionIds.includes(id)
        );

        if (notInOptionIds && notInOptionIds.length) {
          feathersClient
            .service("tenants")
            .find({
              query: {
                id: { $in: notInOptionIds }
              }
            })
            .then((res) => {
              this.conduitAccountOptions.push(
                ...res.map(({ id, name }) => ({
                  key: id,
                  label: name,
                  disabled: false
                }))
              );
            });
        }
      } catch (err) {
        //
      }
    },

    cleanProductPrice(val, index) {
      this.standardProducts[index].price = val.replace(
        /[^0-9.]|(\.(?=.*\.))/g,
        ""
      );
    },

    cleanCustomProductPrice(val, index) {
      this.customProducts[index].price = val.replace(
        /[^0-9.]|(\.(?=.*\.))/g,
        ""
      );
    },

    cleanPartnerPrice(val, index) {
      this.partnerPricing[index].price = val.replace(
        /[^0-9.]|(\.(?=.*\.))/g,
        ""
      );
    }
  },
  mounted() {
    if (this.user) {
      this.findPhoneNumbers();
    }

    if (this.context === "Create") {
      this.addressFieldsActive = false;
    }

    let locationInfo = {
      street_number: "short_name",
      route: "long_name",
      locality: "long_name",
      administrative_area_level_1: "short_name",
      postal_code: "short_name"
    };

    this.autocomplete = new google.maps.places.Autocomplete(
      document.querySelector("input[name='autoAddress']"),
      { types: ["geocode"] }
    );
    this.autocomplete.setFields(["address_components"]);
    this.autocomplete.setComponentRestrictions({
      country: ["us", "vi", "ca"]
    });

    this.autocompleteEdit = new google.maps.places.Autocomplete(
      document.querySelector("input[name='autoAddressEdit']"),
      { types: ["geocode"] }
    );
    this.autocompleteEdit.setFields(["address_components"]);
    this.autocompleteEdit.setComponentRestrictions({
      country: ["us", "vi", "ca"]
    });

    this.autocomplete.addListener("place_changed", () => {
      let place = this.autocomplete.getPlace();
      let ac = place.address_components;

      let locationObject = {
        street_number: "",
        route: "",
        locality: "",
        administrative_area_level_1: "",
        postal_code: ""
      };

      for (let i = 0; i < ac.length; i++) {
        let addressType = ac[i].types[0];
        if (locationInfo[addressType]) {
          locationObject[addressType] = ac[i][locationInfo[addressType]];
        }
      }

      //here we will update our address object sent with the request
      this.populateAddressFields(locationObject);
      this.addressFieldsActive = true;
    });

    this.autocompleteEdit.addListener("place_changed", () => {
      let place = this.autocompleteEdit.getPlace();
      let ac = place.address_components;

      let locationObject = {
        street_number: "",
        route: "",
        locality: "",
        administrative_area_level_1: "",
        postal_code: ""
      };

      for (let i = 0; i < ac.length; i++) {
        let addressType = ac[i].types[0];
        if (locationInfo[addressType]) {
          locationObject[addressType] = ac[i][locationInfo[addressType]];
        }
      }

      //here we will update our address object sent with the request
      this.populateAddressFields(locationObject);
      this.addressFieldsActive = true;
    });
  }
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/variables";

.el-tabs {
  max-width: 600px;
  margin: auto;
  display: block;
}

.integration-checkbox {
  margin-left: 0;
  height: 100px;
  width: 150px;
  display: flex;
  //align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  padding: 0;
  > * {
    display: block;
    flex-basis: 100%;
  }
}

.el-checkbox {
  display: grid;
  grid-template-columns: 30px auto;
  align-items: center;
  margin-left: 0;
  .el-checkbox__input {
    margin-left: 5px !important;
  }
}

.integration-logo {
  //height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    max-width: 65%;
    max-height: 100px;
  }
}

.more-info-icon {
  font-size: 18px;
  color: $voxo-blue;
  margin-left: 5px;
  align-self: flex-end;
  margin-bottom: 5px;
}

.webhook-title {
  margin-bottom: 0;
}

.more-info-icon {
  font-size: 18px;
  color: $voxo-blue;
  margin-left: 5px;
  align-self: flex-end;
  margin-bottom: 5px;
}

.webhook {
  position: relative;

  &__remove-btn {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }
}
</style>
